import * as React from 'react'
import { FlowObjects, DELETE, ADD_MMO } from '../fsm/types'
import { MachineContext } from '../fsm/useMachine'
import { replacer } from '../../model/Action'
import TextFlowObject from '../../model/TextFlowObject'
import ShapeFlowObject from '../../model/ShapeFlowObject'
import ImageFlowObject from '../../model/ImageFlowObject'
import PdfFlowObject from '../../model/PdfFlowObject'
import VideoFlowObject from '../../model/VideoFlowObject'
import GalleryFlowObject from '../../model/GalleryFlowObject'
import WebFlowObject from '../../model/WebFlowObject'
import FlowObject from '../../model/FlowObject'
import guidGenerator from '../utils/commonFunctions'
import { FlowWindow } from '../utils/globals'
import { addImageHelper } from '../control_bar/adding'
import { Alignment } from '../../model/FontAttrsArray'
import { getThemeById } from '../../ai/styles/themeLibrary'

declare let window: FlowWindow;

// Based off of https://github.com/DaveMBush/CrossBrowserCopyAndPaste/blob/master/CrossBrowserCopyAndPaste/App/app.js

export const useClipboard = () => {
    const [current, send] = React.useContext(MachineContext)
    // const [clipboard, setClipboard] = React.useState<(FlowObjects | null)>(null)

    const [systemPasteReady, setSystemPasteReady] = React.useState(false)
    const [systemPasteContent, setSystemPasteContent] = React.useState("")


    const copyHelper = () => {
        // standard way of copying
        var textArea = document.createElement('textarea');
        textArea.setAttribute('style', 'width:1px;border:0;opacity:0;');
        document.body.appendChild(textArea);
        textArea.value = JSON.stringify(current.context.currentObject, replacer);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    }

    const pasteHelper = () => {
        var textArea: HTMLTextAreaElement | null;


        if (window.clipboardData) {
            console.info("retrieving clipboardata", window.clipboardData.getData('Text'))
            return window.clipboardData.getData('Text');
        }
        function waitForPaste() {
            if (!systemPasteReady) {
                setTimeout(waitForPaste, 250);
                return;
            }
            setSystemPasteReady(false)
            document.body.removeChild(textArea!);
            textArea = null;
        }
        // FireFox requires at least one editable 
        // element on the screen for the paste event to fire
        textArea = document.createElement('textarea');
        textArea.setAttribute('style', 'width:1px;border:0;opacity:0;');
        document.body.appendChild(textArea);
        textArea.select();

        waitForPaste();
    }


    const systemPasteListener = (evt: any) => {
        if (current.matches({ textboxes: "inactive" })) {
            // Check for image data in clipboard first
            const items: DataTransferItemList = evt.clipboardData?.items;
            if (items) {
                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.startsWith('image/')) {
                        const blob = items[i].getAsFile();
                        if (blob) {
                            const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
                            addImageHelper(
                                file,
                                current.context.flow.viewSize,
                                send,
                                current.context.index
                            );
                            evt.preventDefault();
                            return;
                        }
                    }
                }
            }
            // Fall through to text handling
            setSystemPasteContent(evt.clipboardData.getData('text/plain'));
            setSystemPasteReady(true);
            evt.preventDefault();
        }
    }

    const cut = () => {
        if (current.context.currentObject) {
            copyHelper()
            send({ type: DELETE, id: current.context.currentObject.id })
        }
    }

    const copy = () => {
        if (current.context.currentObject) {
            copyHelper()
        }
    }

    const paste = () => {
        if (current.matches({ textboxes: "inactive" })) {
            pasteHelper()
            try {
                let content = JSON.parse(systemPasteContent)
                let mmo: FlowObjects;
                if (content.type === "text") {
                    mmo = TextFlowObject.fromJSON(content);
                } else if (content.type === "shape") {
                    mmo = ShapeFlowObject.fromJSON(content);
                } else if (content.type === "image") {
                    mmo = ImageFlowObject.fromJSON(content);
                } else if (content.type === "pdf") {
                    mmo = PdfFlowObject.fromJSON(content);
                } else if (content.type === "video") {
                    mmo = VideoFlowObject.fromJSON(content);
                } else if (content.type === "gallery") {
                    mmo = GalleryFlowObject.fromJSON(content);
                } else if (content.type === "webview") {
                    mmo = WebFlowObject.fromJSON(content);
                } else {
                    mmo = FlowObject.fromJSON(content);
                }
                mmo.id = guidGenerator();
                mmo.frame.x += 5;
                mmo.frame.y += 5;
                send({ type: ADD_MMO, mmo })
            } catch (error) {
                // Non-JSON text — create a new text object from it
                if (systemPasteContent && systemPasteContent.trim().length > 0) {
                    const newText = TextFlowObject.default();
                    newText.text = systemPasteContent;
                    newText.setRange(systemPasteContent.length);

                    // Resolve font from theme, fall back to HelveticaNeue
                    const themeId = current.context.flow.themeId
                        || window.flowboard?.content?.themeId
                        || window.flow_json?.flowboard?.content?.themeId;
                    const theme = themeId ? getThemeById(themeId) : undefined;
                    const fontName = theme?.typography?.bodyFont || 'HelveticaNeue';

                    newText.changeFontName(fontName);
                    newText.changeFontSize(25);
                    newText.changeAlignment(Alignment.CENTER);

                    // Estimate height from text length at font size 25
                    const estimatedLines = Math.max(1, Math.ceil(systemPasteContent.length / 80));
                    newText.frame.height = Math.max(40, estimatedLines * 35);
                    // Center on canvas
                    const viewSize = current.context.flow.viewSize;
                    newText.frame.x = viewSize.width / 2 - newText.frame.width / 2;
                    newText.frame.y = viewSize.height / 2 - newText.frame.height / 2;
                    send({ type: ADD_MMO, mmo: newText, screenIndex: current.context.index });
                }
            }
        }

    }

    return { cut, copy, paste, systemPasteListener };
}